import { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import UploadFile from "components/Input/UploadFile";
import PaymentConflictAlert from "components/Payments/PaymentConflictAlert";
import { PAYMENT_CONFLICT, getPaymentConflict } from "utils/pendingPaymentChange";

export const PAYMENT_METHOD_OPTIONS = [
  { label: "Cash", value: "CASH" },
  { label: "Bank Transfer", value: "BANK_TRANSFER" },
  { label: "Cheque", value: "CHEQUE" },
  { label: "Credit Card", value: "CREDIT_CARD" },
  { label: "Electronic Transfer", value: "ELECTRONIC_TRANSFER" },
  { label: "EFTPOS", value: "EFTPOS" },
  { label: "Online", value: "ONLINE" },
  { label: "UPI", value: "UPI" },
  { label: "PayPal", value: "PAYPAL" },
  { label: "Other", value: "OTHER" },
];

const formFrom = (payment) => ({
  amount: payment?.amount ?? "",
  date: payment?.date ?? "",
  method: payment?.method ?? "",
  reference: payment?.reference ?? "",
  notes: payment?.notes ?? "",
});

const getApiError = (err, fallback) =>
  err?.response?.data?.errorMessage || err?.response?.data?.message || fallback;

/**
 * Lets the sender correct or withdraw a payment while it is still waiting for verification, or
 * review and correct a rejected one before sending it back to the queue. If a
 * verifier got there first, or the payment changed elsewhere, the server refuses with a conflict and
 * this dialog explains it and offers the way forward: carry on from the latest version, resubmit a
 * rejected one with these changes, or record a new payment.
 *
 * The caller supplies the API calls, so the same dialog serves module and term payments:
 *   saveEdit(payment, values) and withdraw(payment, reason) return promises; values is
 *   { amount, date, method, reference, notes, attachments } with payment.revision as the base.
 *
 * @param mode          "edit", "withdraw", or "resubmit" (a rejected payment, sent through onResubmit)
 * @param reasonOnly    a credit move: only its reason can change
 * @param requireMethod the payment cannot be sent without a method
 * @param onChanged   the payment moved on the server (saved, withdrawn, or a conflict showed a newer
 *                    state), so the caller should refresh
 * @param onDone      (mode, values) after a successful save or withdraw, before the dialog closes
 * @param onResubmit  (payment, values) => Promise; used by resubmit mode, and offered in edit mode
 *                    when the payment was rejected meanwhile
 * @param onRecordNew (values) => void; offered when the payment was approved meanwhile
 */
const PendingPaymentDialog = ({
  open,
  mode,
  payment,
  saveEdit,
  withdraw,
  showMethod = false,
  requireMethod = false,
  reasonOnly = false,
  onClose,
  onChanged,
  onDone,
  onResubmit,
  onRecordNew,
}) => {
  const [base, setBase] = useState(payment);
  const [form, setForm] = useState(formFrom(payment));
  const [upload, setUpload] = useState({ attachments: payment?.attachments || [] });
  const [reason, setReason] = useState("");
  const [conflict, setConflict] = useState(null);

  useEffect(() => {
    if (!open) return;
    setBase(payment);
    setForm(formFrom(payment));
    setUpload({ attachments: payment?.attachments || [] });
    setReason("");
    setConflict(null);
  }, [open, payment]);

  const values = () => ({
    amount: String(form.amount).trim(),
    date: form.date,
    method: form.method || null,
    reference: form.reference?.trim() || null,
    notes: form.notes?.trim() || null,
    attachments: (upload.attachments || []).filter(Boolean),
  });

  const handleError = (err, fallback) => {
    const found = getPaymentConflict(err);
    if (!found) {
      toast.error(getApiError(err, fallback));
      return;
    }
    setConflict(found);
    onChanged?.();
  };

  const saveMutation = useMutation({
    mutationFn: () =>
      mode === "withdraw" ? withdraw(base, reason.trim() || null) : saveEdit(base, values()),
    onSuccess: () => {
      toast.success(mode === "withdraw" ? "Payment withdrawn" : "Payment updated");
      onDone?.(mode, values());
      onChanged?.();
      onClose();
    },
    onError: (err) =>
      handleError(err, mode === "withdraw" ? "Failed to withdraw payment" : "Failed to update"),
  });

  const resubmitMutation = useMutation({
    mutationFn: () => onResubmit(base, values()),
    onSuccess: () => {
      toast.success("Payment resubmitted for verification");
      onChanged?.();
      onClose();
    },
    onError: (err) => handleError(err, "Failed to resubmit payment"),
  });

  if (!open || !payment) return null;

  const busy = saveMutation.isLoading || resubmitMutation.isLoading;
  const code = conflict?.code;
  const stillPending = !conflict || code === PAYMENT_CONFLICT.UPDATED;
  const formValid = reasonOnly
    ? !!form.notes?.trim()
    : Number(form.amount) > 0 && !!form.date && (!requireMethod || !!form.method);
  const resubmitting = mode === "resubmit";
  const noun = reasonOnly ? "credit move" : base?.entryType === "REFUND" ? "refund" : "payment";

  /** Keep what the sender typed but move onto the version the server has now. */
  const continueOnLatest = () => {
    setBase(conflict.payment);
    setConflict(null);
  };

  /** Throw away what the sender typed and show the latest version instead. */
  const reloadLatest = () => {
    setBase(conflict.payment);
    setForm(formFrom(conflict.payment));
    setUpload({ attachments: conflict.payment?.attachments || [] });
    setConflict(null);
  };

  const close = () => {
    if (!busy) onClose();
  };

  const setField = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === "withdraw"
          ? `Withdraw ${noun}`
          : resubmitting
          ? `Resubmit rejected ${noun}`
          : `Edit pending ${noun}`}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <PaymentConflictAlert conflict={conflict} />
          {resubmitting && (
            <Alert severity="error">
              Rejected{base?.rejectionFeedback ? `: ${base.rejectionFeedback}` : ""}. Check the
              details below and correct anything that was wrong before resubmitting.
            </Alert>
          )}
          {mode === "withdraw" ? (
            <>
              <Typography variant="body2">
                Withdraw the {noun} of ${Number(base?.amount || 0).toFixed(2)}
                {base?.date ? ` dated ${base.date}` : ""}? It leaves the verification queue and
                never counts towards what the client has paid. This cannot be undone; record it
                again if you need it later.
              </Typography>
              {stillPending && (
                <TextField
                  label="Reason (optional)"
                  size="small"
                  multiline
                  minRows={2}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  inputProps={{ maxLength: 1000 }}
                />
              )}
            </>
          ) : reasonOnly ? (
            <>
              <Typography variant="body2" color="text.secondary">
                The amount and the two terms are what the verifier approves, so only the reason can
                change. Withdraw the move and create it again to move a different amount.
              </Typography>
              <TextField
                label="Reason"
                size="small"
                required
                multiline
                minRows={2}
                value={form.notes}
                onChange={setField("notes")}
              />
            </>
          ) : (
            <>
              <Box className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField
                  label="Amount"
                  type="number"
                  size="small"
                  required
                  value={form.amount}
                  onChange={setField("amount")}
                  inputProps={{ min: 0, step: "0.01" }}
                />
                <TextField
                  label="Date"
                  type="date"
                  size="small"
                  required
                  InputLabelProps={{ shrink: true }}
                  value={form.date}
                  onChange={setField("date")}
                />
                {showMethod && (
                  <Autocomplete
                    options={PAYMENT_METHOD_OPTIONS}
                    value={PAYMENT_METHOD_OPTIONS.find((m) => m.value === form.method) || null}
                    onChange={(_, value) =>
                      setForm((prev) => ({ ...prev, method: value?.value || "" }))
                    }
                    getOptionLabel={(option) => option?.label || ""}
                    renderInput={(params) => (
                      <TextField {...params} label="Payment method" size="small" />
                    )}
                  />
                )}
                <TextField
                  label="Reference / receipt #"
                  size="small"
                  value={form.reference}
                  onChange={setField("reference")}
                />
                <TextField
                  label="Notes"
                  size="small"
                  className="md:col-span-2"
                  multiline
                  minRows={2}
                  value={form.notes}
                  onChange={setField("notes")}
                />
              </Box>
              <UploadFile
                data={upload}
                setData={setUpload}
                imageKey="attachments"
                label="Payment proof / receipts"
                type="multiple"
                newDesign
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>
          {stillPending ? "Cancel" : "Close"}
        </Button>
        {code === PAYMENT_CONFLICT.UPDATED && mode === "edit" && (
          <Button onClick={reloadLatest} disabled={busy}>
            Discard my changes
          </Button>
        )}
        {code === PAYMENT_CONFLICT.UPDATED && (
          <Button variant="contained" onClick={continueOnLatest} disabled={busy}>
            {mode === "edit" ? "Keep my changes" : "Review again"}
          </Button>
        )}
        {code === PAYMENT_CONFLICT.REJECTED && mode === "edit" && onResubmit && (
          <Button
            variant="contained"
            onClick={() => resubmitMutation.mutate()}
            disabled={busy || !formValid}
          >
            {resubmitMutation.isLoading ? "Resubmitting…" : "Resubmit with my changes"}
          </Button>
        )}
        {code === PAYMENT_CONFLICT.VERIFIED && mode === "edit" && onRecordNew && !reasonOnly && (
          <Button
            variant="contained"
            onClick={() => {
              onRecordNew({ ...form, attachments: (upload.attachments || []).filter(Boolean) });
              onClose();
            }}
          >
            Record as a new payment
          </Button>
        )}
        {!conflict && resubmitting && (
          <Button
            variant="contained"
            onClick={() => resubmitMutation.mutate()}
            disabled={busy || !formValid}
          >
            {resubmitMutation.isLoading ? "Resubmitting…" : "Resubmit for verification"}
          </Button>
        )}
        {!conflict && !resubmitting && (
          <Button
            variant="contained"
            color={mode === "withdraw" ? "error" : "primary"}
            onClick={() => saveMutation.mutate()}
            disabled={busy || (mode === "edit" && !formValid)}
          >
            {saveMutation.isLoading
              ? "Working…"
              : mode === "withdraw"
              ? `Withdraw ${noun}`
              : "Save changes"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PendingPaymentDialog;
